import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import purposeLogo from '../../../assets/dashbord_logo.png';
import ShareButton from '../../ui/ShareButton';
import theme from '../../../theme/theme';
import {
  buildPathWithLanguage,
  detectLanguageFromPathname,
  stripLanguagePrefix,
  translate,
} from '../../../i18n';
import { trackEvent } from '../../../utils/analytics';
import { useFeatureFlags } from '../../../contexts/FeatureFlagsContext';

/* eslint-disable no-unused-vars */
function HeaderLogo({ 
  isScrolled,
  isMobile, 
  isSpecialPage, 
  isCompactMode = false,
  onLogoClick = null,
  isControlsCollapsed = false
}) {
  /* eslint-enable no-unused-vars */
  const location = useLocation();
  const flags = useFeatureFlags();
  const currentLanguage = detectLanguageFromPathname(location.pathname);
  const normalizedPath = stripLanguagePrefix(location.pathname);

  const isFunctionPage = normalizedPath === '/function';
  const isPurposePage = normalizedPath === '/purpose';
  const t = (key, params) => translate(currentLanguage, key, params);
  
  const switchButtonConfig = isFunctionPage
    ? {
        href: buildPathWithLanguage('/purpose', currentLanguage, location.pathname),
        label: t('header.toPurpose'),
        color: theme.palette.primary.main,
        hoverBg: 'rgba(74, 85, 104, 0.1)'
      }
    : isPurposePage
    ? {
        href: buildPathWithLanguage('/function', currentLanguage, location.pathname),
        label: t('header.toGraph'),
        color: theme.palette.secondary.main,
        hoverBg: 'rgba(85, 60, 154, 0.1)'
      }
    : null;

  const languageToggle = currentLanguage === 'ja'
    ? {
        to: buildPathWithLanguage(normalizedPath, 'en', location.pathname),
        label: 'EN',
        ariaLabel: t('header.switchToEn'),
      }
    : {
        to: buildPathWithLanguage(normalizedPath, 'ja', location.pathname),
        label: 'JP',
        ariaLabel: t('header.switchToJa'),
      };
  
  // ページに応じてロゴを選択
  const renderLogoContent = () => {
    if (isFunctionPage) {
      return (
        <Box
          sx={{
            height: isMobile ? '34px' : '44px',
            display: 'flex',
            alignItems: 'center',
            color: '#fff'
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: isMobile ? '0.88rem' : '1.1rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: isMobile ? '38vw' : '30vw',
            }}
          >
            {t('header.graphDashboard')}
          </Typography>
        </Box>
      );
    }
    return (
      <Box
        component="img"
        src={purposeLogo}
        alt={t('header.dashboardLogoAlt')}
        sx={{
          height: isMobile ? '34px' : '44px',
          objectFit: 'contain',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />
    );
  };
  
  const handleLogoClick = useCallback((event) => {
    if (!onLogoClick) return;
    event.preventDefault();
    onLogoClick();
  }, [onLogoClick]);

  const renderLogoWrapper = () => {
    if (onLogoClick) {
      return (
        <Box
          component="button"
          type="button"
          onClick={handleLogoClick}
          aria-label={t('header.toggleConditions')}
          sx={{
            border: 'none',
            background: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            zIndex: 10,
            position: 'relative'
          }}
        >
          {renderLogoContent()}
        </Box>
      );
    }

    return (
      <Box
        role="img"
          aria-label={t('header.dashboardLogoAria')}
        sx={{ textDecoration: 'none' }}
      >
        {renderLogoContent()}
      </Box>
    );
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      borderBottom: (isMobile || isSpecialPage) ? 'none' : '1px solid rgba(255,255,255,0.3)',
      py: isMobile ? 0.5 : 0.6,
      px: isMobile ? 2 : 2.5,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    }}>
      {/* 左側：ダッシュボード切り替えボタン */}
      <Box sx={{ width: isMobile ? '80px' : '120px', display: 'flex', justifyContent: 'flex-start' }}>
        {switchButtonConfig && (
          <Button
            component={Link}
            to={switchButtonConfig.href}
            onClick={() => {
              const fromMode = isFunctionPage ? 'function' : 'purpose';
              const toMode = isFunctionPage ? 'purpose' : 'function';
              trackEvent('switch_ui_mode', {
                from_ui_mode: fromMode,
                to_ui_mode: toMode,
                source: 'header_toggle',
              });
            }}
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            sx={{
              fontSize: isMobile ? '0.65rem' : '0.75rem',
              px: isMobile ? 0.8 : 1.2,
              py: isMobile ? 0.4 : 0.6,
              width: '100%',
              minWidth: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: switchButtonConfig.color,
              borderColor: switchButtonConfig.color,
              textDecoration: 'none',
              '&:hover': {
                backgroundColor: switchButtonConfig.hoverBg,
                textDecoration: 'none',
              },
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {switchButtonConfig.label}
          </Button>
        )}
      </Box>
      
      {/* 中央のロゴ（幅を制約してヘッダー全体の崩れを防止） */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          justifyContent: 'center',
          px: isMobile ? 1 : 2,
        }}
      >
        {renderLogoWrapper()}
      </Box>
      
      {/* 右側の共有ボタン */}
      {!isSpecialPage && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 0.6 : 1,
          }}
        >
          {flags.english && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                component={Link}
                to={languageToggle.to}
                variant="outlined"
                size="small"
                aria-label={languageToggle.ariaLabel}
                sx={{
                  minWidth: isMobile ? '36px' : '44px',
                  px: isMobile ? 0.8 : 1,
                  py: isMobile ? 0.2 : 0.3,
                  fontSize: isMobile ? '0.65rem' : '0.72rem',
                  lineHeight: 1.1,
                  color: '#fff',
                  backgroundColor: 'transparent',
                  borderColor: 'rgba(255, 255, 255, 0.85)',
                  '&:hover': {
                    borderColor: '#fff',
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  },
                }}
              >
                {languageToggle.label}
              </Button>
            </Box>
          )}
          <Box sx={{
            '& .MuiButton-root': {
              height: isMobile ? '28px' : '36px',
              minWidth: isMobile ? '60px' : '80px',
              fontSize: isMobile ? '0.75rem' : '0.85rem',
              padding: isMobile ? '4px 8px' : '6px 12px',
              '& .MuiButton-startIcon': {
                marginRight: isMobile ? '4px' : '6px',
                '& svg': {
                  fontSize: isMobile ? '1rem' : '1.1rem'
                }
              }
            }
          }}>
            <ShareButton
              variant='button'
              size='small'
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

HeaderLogo.propTypes = {
  isScrolled: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isSpecialPage: PropTypes.bool.isRequired,
  isCompactMode: PropTypes.bool,
  onLogoClick: PropTypes.func,
  isControlsCollapsed: PropTypes.bool,
};

export default HeaderLogo;
